import React, { useState, useContext, useEffect, useMemo } from 'react';
import {
  Box,
  TextField,
  Button,
  Chip,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import { SearchOutlined, FileDownloadOutlined, CloseOutlined } from '@mui/icons-material';
import Header from '../../components/Header';
import { FilterContext } from '../../contexts/FilterContext';
import { exportToExcel } from '../../helpers/exportFuncs';
import { ResponsiveBar } from '@nivo/bar';
import axios from 'axios';
import moment from 'moment';

// --- Types ---

interface EmailMessage {
  date: string;
  subject: string;
  from_name: string;
  esp: 'iterable' | 'mailchimp';
  category: string;
  campaign_id: string;
  opened: boolean | null;
}

interface Summary {
  total: number;
  by_category: Record<string, number>;
  by_day: Record<string, number>;
  daily_average: number;
}

interface LookupResult {
  email: string;
  messages: EmailMessage[];
  summary: Summary;
}

interface DailyCount {
  date: string;
  newsletters: number;
  sams: number;
  non_openers: number;
  webinar_mailings: number;
  plx_mailings: number;
  followups: number;
  reminders: number;
  total: number;
}

interface CategoryKpi {
  category: string;
  campaigns: number;
  avg_recipients: number;
  avg_opens: number;
  avg_clicks: number;
  avg_open_rate: number;
  avg_click_rate: number;
  avg_bounces: number;
  avg_unsubscribes: number;
  avg_unsub_rate: number;
}

interface OverviewResult {
  daily_counts: DailyCount[];
  averages: {
    daily_total: number;
    daily_reminders: number;
    weekly: number;
  };
  category_kpis: CategoryKpi[];
  reminder_forecast: {
    webinars_active: number;
    reminders_per_week: number;
    detail: {
      name: string;
      occurrences_in_range: number;
      reminders_per_occurrence: number;
      total_reminders: number;
    }[];
  };
}

// --- Category Config ---

const CATEGORY_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  newsletter: { label: 'Newsletter', color: '#22c55e', bg: '#f0fdf4' },
  sam: { label: 'SAM', color: '#a855f7', bg: '#faf5ff' },
  non_opener: { label: 'Non-Opener', color: '#f97316', bg: '#fff7ed' },
  heatup: { label: 'Heat-Up', color: '#3b82f6', bg: '#eff6ff' },
  redlink: { label: 'Redlink', color: '#ef4444', bg: '#fef2f2' },
  plx_mailing: { label: 'PLX Mailing', color: '#eab308', bg: '#fefce8' },
  webinar_reminder: {
    label: 'Webinar Reminder',
    color: '#06b6d4',
    bg: '#ecfeff',
  },
  followup: { label: 'Followup', color: '#8b5cf6', bg: '#f5f3ff' },
  other: { label: 'Andere', color: '#6b7280', bg: '#f9fafb' },
  other_mc: { label: 'MC Andere', color: '#6b7280', bg: '#f9fafb' },
};

function getCategoryConfig(category: string) {
  return CATEGORY_CONFIG[category] || CATEGORY_CONFIG.other;
}

const REMINDER_CATEGORIES = new Set(['webinar_reminder']);

interface DayCampaign {
  campaign_id: number;
  time: string;
  subject: string;
  from_name: string;
  category: string;
  recipients: number;
  unique_opens: number;
  unique_clicks: number;
  open_rate: number;
  click_rate: number;
  bounces: number;
  unsubscribes: number;
  complaints: number;
}

// --- Day Detail Modal ---

const DayDetailModal = ({ date, onClose }: { date: string | null; onClose: () => void }) => {
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState<DayCampaign[]>([]);

  useEffect(() => {
    if (!date) return;
    let cancelled = false;
    const fetchDay = async () => {
      setLoading(true);
      try {
        const url = (process.env.REACT_APP_API_BASE_URL || '') + `/api/email-history/day/${date}`;
        const response = await axios.get(url);
        if (!cancelled && response.data?.success) {
          setCampaigns(response.data.data.campaigns);
        }
      } catch (err) {
        console.error('Day detail fetch error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchDay();
    return () => {
      cancelled = true;
    };
  }, [date]);

  const totalRecipients = campaigns.reduce((s, c) => s + c.recipients, 0);
  const totalOpens = campaigns.reduce((s, c) => s + c.unique_opens, 0);
  const totalClicks = campaigns.reduce((s, c) => s + c.unique_clicks, 0);

  return (
    <Dialog open={!!date} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        Kampagnen am {date ? moment(date).format('DD.MM.YYYY (dddd)') : ''}
        <IconButton onClick={onClose} size="small">
          <CloseOutlined />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* KPI summary */}
            <Box display="flex" gap={2} mb={2} flexWrap="wrap">
              <SummaryCard label="Kampagnen" value={campaigns.length} />
              <SummaryCard
                label="Empfänger (Ø)"
                value={
                  campaigns.length > 0
                    ? Math.round(totalRecipients / campaigns.length).toLocaleString('de-DE')
                    : 0
                }
              />
              <SummaryCard
                label="Ø Open Rate"
                value={
                  totalRecipients > 0
                    ? `${Math.round((totalOpens / totalRecipients) * 1000) / 10}%`
                    : '—'
                }
              />
              <SummaryCard
                label="Ø Click Rate"
                value={
                  totalRecipients > 0
                    ? `${Math.round((totalClicks / totalRecipients) * 1000) / 10}%`
                    : '—'
                }
              />
            </Box>

            <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', maxHeight: 500 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {[
                      'Zeit',
                      'Betreff',
                      'Von',
                      'Typ',
                      'Empfänger',
                      'Opens',
                      'Open %',
                      'Clicks',
                      'Click %',
                      'Bounces',
                      'Unsubs',
                    ].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          backgroundColor: '#e0e0e0 !important',
                          color: 'black',
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap',
                        }}
                        align={['Betreff', 'Von', 'Typ', 'Zeit'].includes(h) ? 'left' : 'right'}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {campaigns.map((c) => {
                    const config = getCategoryConfig(c.category);
                    return (
                      <TableRow
                        key={c.campaign_id}
                        sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}
                      >
                        <TableCell>{moment(c.time).format('HH:mm')}</TableCell>
                        <TableCell sx={{ maxWidth: 300 }}>
                          <Typography variant="body2" noWrap title={c.subject}>
                            {c.subject}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {c.from_name || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={config.label}
                            size="small"
                            sx={{
                              backgroundColor: config.bg,
                              color: config.color,
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              border: `1px solid ${config.color}30`,
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">{c.recipients.toLocaleString('de-DE')}</TableCell>
                        <TableCell align="right">
                          {c.unique_opens.toLocaleString('de-DE')}
                        </TableCell>
                        <TableCell align="right">{c.open_rate}%</TableCell>
                        <TableCell align="right">
                          {c.unique_clicks.toLocaleString('de-DE')}
                        </TableCell>
                        <TableCell align="right">{c.click_rate}%</TableCell>
                        <TableCell align="right">{c.bounces}</TableCell>
                        <TableCell align="right">{c.unsubscribes}</TableCell>
                      </TableRow>
                    );
                  })}
                  {campaigns.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={11} align="center">
                        <Typography color="text.secondary" py={2}>
                          Keine Kampagnen an diesem Tag
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// --- Lookup Tab ---

const LookupTab = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<LookupResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeCategories, setActiveCategories] = useState<Set<string>>(new Set());
  const [includeReminders, setIncludeReminders] = useState(true);

  const handleSearch = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const url =
        (process.env.REACT_APP_API_BASE_URL || '') +
        `/api/email-history/${encodeURIComponent(trimmed)}`;
      const response = await axios.get(url);

      if (response.data?.success) {
        setResult(response.data.data);
        setActiveCategories(new Set());
      } else {
        setError(response.data?.error || 'Unbekannter Fehler');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Verbindungsfehler');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const toggleCategory = (category: string) => {
    setActiveCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const filteredMessages = useMemo(() => {
    if (!result) return [];
    return result.messages.filter((m) => {
      if (!includeReminders && REMINDER_CATEGORIES.has(m.category)) return false;
      if (activeCategories.size > 0 && !activeCategories.has(m.category)) return false;
      return true;
    });
  }, [result, activeCategories, includeReminders]);

  const handleExport = () => {
    if (!filteredMessages.length) return;
    const exportData = filteredMessages.map((m) => ({
      Datum: moment(m.date).format('DD.MM.YYYY HH:mm'),
      Betreff: m.subject,
      Von: m.from_name,
      ESP: m.esp === 'iterable' ? 'Iterable' : 'Mailchimp',
      Kategorie: getCategoryConfig(m.category).label,
      Geöffnet: m.opened === true ? 'Ja' : m.opened === false ? 'Nein' : '',
    }));
    exportToExcel(exportData, `email-verlauf-${result?.email || 'export'}`);
  };

  return (
    <Box>
      {/* Search bar */}
      <Box display="flex" gap={2} mb={3} alignItems="center">
        <TextField
          label="E-Mail-Adresse"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={handleKeyDown}
          variant="outlined"
          size="small"
          sx={{ minWidth: 350 }}
          placeholder="z.B. streit.aachen@web.de"
        />
        <Button
          variant="contained"
          onClick={handleSearch}
          disabled={loading || !email.trim()}
          startIcon={loading ? <CircularProgress size={18} /> : <SearchOutlined />}
        >
          Suchen
        </Button>
        <FormControlLabel
          control={
            <Switch
              checked={includeReminders}
              onChange={(e) => setIncludeReminders(e.target.checked)}
              size="small"
            />
          }
          label="Mit Webinar Reminders"
          sx={{ ml: 2 }}
        />
      </Box>

      {error && (
        <Typography color="error" mb={2}>
          {error}
        </Typography>
      )}

      {result && (
        <>
          {/* Summary cards */}
          <Box display="flex" gap={2} mb={3} flexWrap="wrap">
            <SummaryCard label="Gesamt" value={filteredMessages.length} />
            <SummaryCard
              label="Ø pro Tag"
              value={
                Object.keys(result.summary.by_day).length > 0
                  ? Math.round(
                      (filteredMessages.length / Object.keys(result.summary.by_day).length) * 10,
                    ) / 10
                  : 0
              }
            />
            <SummaryCard label="Tage" value={Object.keys(result.summary.by_day).length} />
            <SummaryCard
              label="Iterable"
              value={result.messages.filter((m) => m.esp === 'iterable').length}
            />
            <SummaryCard
              label="Mailchimp"
              value={result.messages.filter((m) => m.esp === 'mailchimp').length}
            />
          </Box>

          {/* Daily frequency chart */}
          {Object.keys(result.summary.by_day).length > 1 && (
            <Box
              sx={{
                backgroundColor: '#ffffff',
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                p: 2,
                mb: 3,
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                E-Mails pro Tag
              </Typography>
              <Box height="250px">
                <ResponsiveBar
                  data={Object.entries(result.summary.by_day)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([date, count]) => ({
                      date: moment(date).format('DD.MM.'),
                      count,
                    }))}
                  keys={['count']}
                  indexBy="date"
                  margin={{ top: 10, right: 20, bottom: 40, left: 40 }}
                  padding={0.3}
                  colors={['#3b82f6']}
                  axisBottom={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: -30,
                  }}
                  axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                    format: (v) => (Number.isInteger(v) ? String(v) : ''),
                  }}
                  labelSkipWidth={20}
                  labelSkipHeight={12}
                  labelTextColor="#ffffff"
                  tooltip={({ indexValue, value }) => (
                    <Box
                      sx={{
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: 1,
                        px: 1.5,
                        py: 0.5,
                      }}
                    >
                      <strong>{indexValue}</strong>: {value} E-Mails
                    </Box>
                  )}
                />
              </Box>
            </Box>
          )}

          {/* Category filter chips */}
          <Box display="flex" gap={1} mb={2} flexWrap="wrap">
            {Object.entries(result.summary.by_category)
              .sort(([, a], [, b]) => b - a)
              .map(([category, count]) => {
                const config = getCategoryConfig(category);
                const isActive = activeCategories.size === 0 || activeCategories.has(category);
                return (
                  <Chip
                    key={category}
                    label={`${config.label} (${count})`}
                    onClick={() => toggleCategory(category)}
                    sx={{
                      backgroundColor: isActive ? config.color : '#e5e7eb',
                      color: isActive ? '#fff' : '#6b7280',
                      fontWeight: isActive ? 600 : 400,
                      '&:hover': {
                        backgroundColor: isActive ? config.color : '#d1d5db',
                      },
                    }}
                  />
                );
              })}
          </Box>

          {/* Excel export */}
          <Box display="flex" justifyContent="flex-end" mb={1}>
            <Tooltip title="Excel Export">
              <IconButton onClick={handleExport} size="small">
                <FileDownloadOutlined />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Messages table */}
          <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      color: 'black',
                      fontWeight: 'bold',
                      minWidth: 140,
                    }}
                  >
                    Datum
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      color: 'black',
                      fontWeight: 'bold',
                    }}
                  >
                    Betreff
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      color: 'black',
                      fontWeight: 'bold',
                      minWidth: 160,
                    }}
                  >
                    Von
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      color: 'black',
                      fontWeight: 'bold',
                      minWidth: 80,
                    }}
                  >
                    ESP
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      color: 'black',
                      fontWeight: 'bold',
                      minWidth: 130,
                    }}
                  >
                    Kategorie
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMessages.map((msg, idx) => {
                  const config = getCategoryConfig(msg.category);
                  return (
                    <TableRow
                      key={`${msg.campaign_id}-${idx}`}
                      sx={{
                        '&:hover': { backgroundColor: '#f5f5f5' },
                      }}
                    >
                      <TableCell>{moment(msg.date).format('DD.MM.YYYY HH:mm')}</TableCell>
                      <TableCell sx={{ maxWidth: 400 }}>
                        <Typography variant="body2" noWrap title={msg.subject}>
                          {msg.subject}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {msg.from_name || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={msg.esp === 'iterable' ? 'Iterable' : 'MC'}
                          size="small"
                          sx={{
                            backgroundColor: msg.esp === 'iterable' ? '#dbeafe' : '#fef3c7',
                            color: msg.esp === 'iterable' ? '#1d4ed8' : '#92400e',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={config.label}
                          size="small"
                          sx={{
                            backgroundColor: config.bg,
                            color: config.color,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            border: `1px solid ${config.color}30`,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredMessages.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary" py={3}>
                        {activeCategories.size > 0
                          ? 'Keine E-Mails in dieser Kategorie'
                          : 'Keine E-Mails gefunden'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="body2" color="text.secondary" mt={1}>
            {filteredMessages.length} von {result.summary.total} E-Mails angezeigt
            {activeCategories.size > 0 && ' (gefiltert)'}
          </Typography>
        </>
      )}
    </Box>
  );
};

// --- Overview Tab ---

const OverviewTab = () => {
  const { filterState } = useContext(FilterContext)!;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<OverviewResult | null>(null);
  const [includeReminders, setIncludeReminders] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const startDate = useMemo(
    () => moment(filterState.startDate).format('YYYY-MM-DD'),
    [filterState.startDate],
  );
  const endDate = useMemo(
    () => moment(filterState.endDate).format('YYYY-MM-DD'),
    [filterState.endDate],
  );

  useEffect(() => {
    let cancelled = false;
    const fetchOverview = async () => {
      setLoading(true);
      try {
        const url =
          (process.env.REACT_APP_API_BASE_URL || '') +
          `/api/email-history/overview?startDate=${startDate}&endDate=${endDate}`;
        const response = await axios.get(url);
        if (!cancelled && response.data?.success) {
          setData(response.data.data);
        }
      } catch (err) {
        console.error('Overview fetch error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchOverview();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  const chartData = useMemo(
    () =>
      data
        ? data.daily_counts.map((d) => ({
            date: moment(d.date).format('DD.MM.'),
            fullDate: d.date,
            newsletters: d.newsletters,
            sams: d.sams,
            non_openers: d.non_openers,
            webinar_mailings: d.webinar_mailings,
            plx_mailings: d.plx_mailings,
            followups: d.followups,
            reminders: includeReminders ? d.reminders : 0,
          }))
        : [],
    [data, includeReminders],
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) return null;

  const OVERVIEW_KEYS = includeReminders
    ? [
        'newsletters',
        'sams',
        'non_openers',
        'webinar_mailings',
        'plx_mailings',
        'followups',
        'reminders',
      ]
    : ['newsletters', 'sams', 'non_openers', 'webinar_mailings', 'plx_mailings', 'followups'];
  const OVERVIEW_COLORS: Record<string, string> = {
    newsletters: '#22c55e',
    sams: '#a855f7',
    non_openers: '#f97316',
    webinar_mailings: '#3b82f6',
    plx_mailings: '#eab308',
    followups: '#8b5cf6',
    reminders: '#06b6d4',
  };
  const OVERVIEW_LABELS: Record<string, string> = {
    newsletters: 'Newsletter',
    sams: 'SAMs',
    non_openers: 'Non-Opener',
    webinar_mailings: 'Webinar Mailings',
    plx_mailings: 'PLX Mailings',
    followups: 'Followups',
    reminders: 'Reminder',
  };

  return (
    <Box>
      {/* Trigger toggle */}
      <Box mb={2}>
        <FormControlLabel
          control={
            <Switch
              checked={includeReminders}
              onChange={(e) => setIncludeReminders(e.target.checked)}
              size="small"
            />
          }
          label="Mit Webinar Reminders"
        />
      </Box>

      {/* Average cards */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <SummaryCard label="Ø Mails / Tag" value={data.averages.daily_total} />
        <SummaryCard label="Ø Reminder / Tag" value={data.averages.daily_reminders} />
        <SummaryCard label="Ø pro Woche" value={data.averages.weekly} />
        <SummaryCard label="Aktive Webinare" value={data.reminder_forecast.webinars_active} />
        <SummaryCard label="Reminder / Woche" value={data.reminder_forecast.reminders_per_week} />
      </Box>

      {/* Stacked bar chart */}
      <Box
        sx={{
          backgroundColor: '#ffffff',
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          p: 2,
          mb: 3,
        }}
      >
        <Typography variant="subtitle2" gutterBottom>
          E-Mails pro Tag nach Kategorie (gestapelt)
        </Typography>
        <Box height="350px">
          <ResponsiveBar
            data={chartData}
            keys={OVERVIEW_KEYS}
            indexBy="date"
            margin={{ top: 10, right: 160, bottom: 50, left: 50 }}
            padding={0.3}
            groupMode="stacked"
            colors={(bar) => OVERVIEW_COLORS[bar.id as string] || '#6b7280'}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: -30,
              legend: 'Datum',
              legendPosition: 'middle',
              legendOffset: 42,
            }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              legend: 'Anzahl E-Mails',
              legendPosition: 'middle',
              legendOffset: -40,
              format: (v) => (Number.isInteger(v) ? String(v) : ''),
            }}
            onClick={(bar) => {
              const item = chartData.find((d) => d.date === bar.indexValue);
              if (item?.fullDate) setSelectedDate(item.fullDate);
            }}
            labelSkipWidth={16}
            labelSkipHeight={16}
            labelTextColor="#ffffff"
            legends={[
              {
                dataFrom: 'keys',
                anchor: 'bottom-right',
                direction: 'column',
                translateX: 150,
                translateY: 0,
                itemsSpacing: 2,
                itemWidth: 140,
                itemHeight: 20,
                itemDirection: 'left-to-right',
                symbolSize: 12,
                symbolShape: 'square',
                data: OVERVIEW_KEYS.map((key) => ({
                  id: key,
                  label: OVERVIEW_LABELS[key],
                  color: OVERVIEW_COLORS[key],
                })),
              },
            ]}
            tooltip={({ id, value, indexValue }) => (
              <Box
                sx={{
                  backgroundColor: '#fff',
                  border: '1px solid #ccc',
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.5,
                }}
              >
                <strong>{indexValue}</strong> — {OVERVIEW_LABELS[id as string] || id}: {value}
              </Box>
            )}
          />
        </Box>
      </Box>

      {/* Category KPI averages */}
      {data.category_kpis && data.category_kpis.length > 0 && (
        <Box mb={3}>
          <Typography variant="subtitle2" gutterBottom>
            Durchschnittliche KPIs pro Mailing-Typ
          </Typography>
          <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[
                    'Typ',
                    'Kampagnen',
                    'Ø Empfänger',
                    'Ø Opens',
                    'Ø Open %',
                    'Ø Clicks',
                    'Ø Click %',
                    'Ø Bounces',
                    'Ø Unsubs',
                    'Unsub %',
                  ].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        backgroundColor: '#e0e0e0 !important',
                        color: 'black',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                      }}
                      align={h === 'Typ' ? 'left' : 'right'}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.category_kpis.map((k) => {
                  const config = getCategoryConfig(k.category);
                  return (
                    <TableRow key={k.category}>
                      <TableCell>
                        <Chip
                          label={config.label}
                          size="small"
                          sx={{
                            backgroundColor: config.bg,
                            color: config.color,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            border: `1px solid ${config.color}30`,
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">{k.campaigns}</TableCell>
                      <TableCell align="right">
                        {k.avg_recipients.toLocaleString('de-DE')}
                      </TableCell>
                      <TableCell align="right">{k.avg_opens.toLocaleString('de-DE')}</TableCell>
                      <TableCell align="right">{k.avg_open_rate}%</TableCell>
                      <TableCell align="right">{k.avg_clicks.toLocaleString('de-DE')}</TableCell>
                      <TableCell align="right">{k.avg_click_rate}%</TableCell>
                      <TableCell align="right">{k.avg_bounces}</TableCell>
                      <TableCell align="right">{k.avg_unsubscribes}</TableCell>
                      <TableCell align="right">{k.avg_unsub_rate}%</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Reminder forecast table */}
      {data.reminder_forecast.detail.length > 0 && (
        <Box mb={3}>
          <Typography variant="subtitle2" gutterBottom>
            Webinar Reminder Forecast (MC-basiert, pro registriertem Nutzer)
          </Typography>
          <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', maxWidth: 600 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Webinar', 'Termine im Zeitraum', 'Reminder gesamt'].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        backgroundColor: '#e0e0e0 !important',
                        color: 'black',
                        fontWeight: 'bold',
                      }}
                      align={h === 'Webinar' ? 'left' : 'center'}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.reminder_forecast.detail.map((w) => (
                  <TableRow key={w.name}>
                    <TableCell>{w.name}</TableCell>
                    <TableCell align="center">{w.occurrences_in_range}</TableCell>
                    <TableCell align="center">{w.total_reminders}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Gesamt</TableCell>
                  <TableCell align="center">—</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    {data.reminder_forecast.reminders_per_week}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Daily counts table */}
      <Typography variant="subtitle2" gutterBottom>
        Tagesdetails
      </Typography>
      <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', maxHeight: 400 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {[
                'Datum',
                'Newsletter',
                'SAMs',
                'Non-Opener',
                'Webinar',
                'PLX',
                'Followups',
                'Reminder',
                'Gesamt',
              ].map((h) => (
                <TableCell
                  key={h}
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    color: 'black',
                    fontWeight: 'bold',
                  }}
                  align={h === 'Datum' ? 'left' : 'center'}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.daily_counts.map((d) => (
              <TableRow
                key={d.date}
                onClick={() => setSelectedDate(d.date)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: '#f0f4ff' },
                }}
              >
                <TableCell>{moment(d.date).format('DD.MM.YYYY')}</TableCell>
                <TableCell align="center">{d.newsletters || '—'}</TableCell>
                <TableCell align="center">{d.sams || '—'}</TableCell>
                <TableCell align="center">{d.non_openers || '—'}</TableCell>
                <TableCell align="center">{d.webinar_mailings || '—'}</TableCell>
                <TableCell align="center">{d.plx_mailings || '—'}</TableCell>
                <TableCell align="center">{d.followups || '—'}</TableCell>
                <TableCell align="center">{d.reminders || '—'}</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                  {d.total}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Day detail modal */}
      <DayDetailModal date={selectedDate} onClose={() => setSelectedDate(null)} />
    </Box>
  );
};

// --- Summary Card ---

const SummaryCard = ({ label, value }: { label: string; value: number | string }) => (
  <Box
    sx={{
      backgroundColor: '#ffffff',
      border: '1px solid #e0e0e0',
      borderRadius: 2,
      px: 3,
      py: 2,
      minWidth: 120,
      textAlign: 'center',
    }}
  >
    <Typography variant="h4" fontWeight="bold">
      {value}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

// --- Main Page ---

const EmailHistory = () => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Box m="20px">
      <Header title="E-MAIL VERLAUF" subtitle="Versandhistorie & Frequenzanalyse" />

      <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="Versandübersicht" />
        <Tab label="Empfänger-Lookup" />
      </Tabs>

      {activeTab === 0 && <OverviewTab />}
      {activeTab === 1 && <LookupTab />}
    </Box>
  );
};

export default EmailHistory;
